/**
 * A chat answer, rendered.
 *
 * Lifted out of `ModelChatPanel` so the model rail and the tenant-level ask surface on
 * the models page render an answer the same way. Two copies of this would drift on the
 * three guards below, and each of those cost a real bug once already.
 */
import React from 'react';
import ReactMarkdown, { uriTransformer } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { isRenderableHref, studioLinkUri, studioTarget } from './chatParts';

// An answer is markdown (lists, tables, code, inline references) rendered as real
// React elements.
//
// Not an HTML string through `dangerouslySetInnerHTML`, which is what this was. That
// path needed marked to build HTML, DOMPurify to sanitize it, and DOMPurify's URI
// scheme allowlist widened so a `studio:` href would survive at all. Widening a
// sanitizer to carry application data is fine right up until somebody forgets why it
// was widened. react-markdown never builds HTML, so raw markup in a model's output is
// inert by default and a reference is a component rather than an href that has to
// survive a filter.
//
// `remarkGfm` is what makes tables work, and the agent writes tables.
export function Markdown({
  text,
  onFocus,
}: {
  text: string;
  onFocus?: (targetType: string, targetId: string) => void;
}) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      // Without this a `studio:` reference never survives to the `a` component
      // below: the default transformer rewrites it to `javascript:void(0)`, which
      // then rendered as an external link and opened a blank tab.
      transformLinkUri={(uri) => studioLinkUri(uri, uriTransformer)}
      components={{
        a: ({ href, children }) => {
          // A reference to something in Studio: select it in place rather than
          // navigating away. `studioTarget` rejects anything that is not a bare type
          // and id, and such a link falls through to being an ordinary one.
          const target = studioTarget(href ?? '');
          if (target && onFocus) {
            return (
              <a
                href={href}
                onClick={(event) => {
                  event.preventDefault();
                  onFocus(target.targetType, target.targetId);
                }}
              >
                {children}
              </a>
            );
          }
          // A blocked scheme comes back as `javascript:void(0)`, which is inert but
          // still opens an empty tab. Render it as its own text instead of a link
          // that looks like the app breaking.
          if (!isRenderableHref(href)) return <>{children}</>;
          // Anything else leaves Studio, so it opens away from the app and cannot
          // reach back through window.opener.
          return (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          );
        },
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

export default Markdown;
